import { motion, useInView, useSpring, AnimatePresence } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import { Link } from "@tanstack/react-router";
import { Navbar, Footer } from "@/components/site-chrome";
import { Button } from "@/components/ui/button";
import {
  ShieldIcon, BookIcon, TransportIcon, MessageSquareIcon,
  ChartIcon, SparkleIcon, ArrowRightIcon, GrowthIcon,
  DeviceIcon, MealIcon, ReceiptIcon, DocumentIcon,
  CheckIcon, WalletIcon,
} from "@/components/ui/TriIcons";

const EASE = [0.4, 0, 0.2, 1] as const;
const WINE = "#7B1416";
const SAND = "#C4A882";
const CHARCOAL = "#1C1917";
const CREAM = "#F5EFE4";
const CREAM2 = "#EDE4D4";
const FONT_DISPLAY = "'Georgia', 'Times New Roman', 'Playfair Display', serif";
const FONT_SANS = "-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', system-ui, sans-serif";
const GRAIN = `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`;

const products = [
  { id: "identity", name: "Identity", icon: ShieldIcon, color: WINE, desc: "Digital identity for every student, verified across every system." },
  { id: "learn", name: "Learn", icon: BookIcon, color: SAND, desc: "Digital learning with grades, assignments, and progress tracking." },
  { id: "transit", name: "Transit", icon: TransportIcon, color: WINE, desc: "Real-time GPS tracking with student boarding verification." },
  { id: "connect", name: "Connect", icon: MessageSquareIcon, color: SAND, desc: "Unified parent-school messaging across app, SMS, and WhatsApp." },
  { id: "campus", name: "Campus", icon: MealIcon, color: WINE, desc: "Meals, marketplace, library, and campus experiences in one wallet." },
  { id: "intelligence", name: "Intelligence", icon: SparkleIcon, color: SAND, desc: "AI predictions and live analytics for every stakeholder." },
  { id: "government", name: "Government", icon: DocumentIcon, color: WINE, desc: "Automated compliance reporting and ministry submissions." },
  { id: "pay", name: "Pay", icon: ReceiptIcon, color: SAND, desc: "Every payment method, one checkout, instant reconciliation." },
];

const flow = ["Parents", "Students", "Teachers", "Schools", "Merchants", "Banks", "Mobile Money", "Transport", "Government", "Analytics", "AI"];

export function EcosystemPage() {
  return (
    <div className="relative min-h-screen overflow-hidden" style={{ background: CREAM, fontFamily: FONT_SANS }}>
      {/* Grain + dot grid */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.035]" style={{ backgroundImage: GRAIN, backgroundSize: "200px 200px" }} />
      <div className="pointer-events-none absolute inset-0 opacity-50" style={{ backgroundImage: `radial-gradient(circle, ${CHARCOAL}0A 1px, transparent 1px)`, backgroundSize: "32px 32px" }} />
      <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: `linear-gradient(90deg, ${WINE}, ${SAND}, ${WINE})` }} />
      <div className="relative z-10">
      <Navbar />
      <EcosystemHero />
      <ProductGrid />
      <FlowSection />
      <ArchitectureSection />
      <ScaleSection />
      <CTASection />
      </div>
      <Footer />
    </div>
  );
}

function EcosystemHero() {
  return (
    <section className="relative overflow-hidden pt-32 pb-24 sm:pt-40 sm:pb-32" style={{ background: CREAM }}>
      <div className="pointer-events-none absolute inset-0 opacity-[0.035]" style={{ backgroundImage: GRAIN, backgroundSize: "200px 200px" }} />
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `
            radial-gradient(ellipse 80% 50% at 50% 0%, ${WINE}10 0%, transparent 60%),
            radial-gradient(ellipse 60% 40% at 20% 60%, ${SAND}12 0%, transparent 50%)
          `,
        }}
      />
      <div className="relative z-10 mx-auto max-w-4xl px-6 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
          <div className="mb-8 flex items-center justify-center gap-4">
            <div className="h-px w-12" style={{ background: WINE }} />
            <span className="text-[11px] font-bold uppercase tracking-[0.3em]" style={{ color: WINE, fontFamily: FONT_SANS }}>
              The TriSphere Ecosystem
            </span>
            <div className="h-px w-12" style={{ background: WINE }} />
          </div>
          <h1 style={{ fontFamily: FONT_DISPLAY, color: CHARCOAL, lineHeight: 1.02, letterSpacing: "-0.03em", fontSize: "clamp(2.5rem, 7vw, 5rem)", fontWeight: 700 }}>
            One platform.
          </h1>
          <h1 style={{ fontFamily: FONT_DISPLAY, color: WINE, lineHeight: 1.02, letterSpacing: "-0.03em", fontSize: "clamp(2.5rem, 7vw, 5rem)", fontWeight: 700, fontStyle: "italic" }}>
            Infinite connections.
          </h1>
          <p className="mx-auto mt-8 max-w-2xl text-lg leading-[1.7]" style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", color: `${CHARCOAL}80` }}>
            Explore every layer of the ecosystem — from digital identity to AI intelligence.
            Eight products, one ledger, zero disconnects.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

function ProductGrid() {
  const [activeId, setActiveId] = useState("identity");
  const active = products.find(p => p.id === activeId)!;
  const ActiveIcon = active.icon;

  return (
    <section className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-6xl px-6 sm:px-8">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {products.map((p, i) => {
            const isActive = p.id === activeId;
            return (
              <motion.button
                key={p.id}
                onClick={() => setActiveId(p.id)}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.4 }}
                className="group relative overflow-hidden rounded-2xl border text-left transition-all duration-300"
                style={{
                  borderColor: isActive ? p.color : `${CHARCOAL}15`,
                  background: isActive ? `${p.color}08` : CREAM,
                  boxShadow: isActive ? `0 8px 32px ${p.color}15` : "none",
                }}
              >
                <div className="p-6">
                  <div className="grid h-12 w-12 place-items-center rounded-xl transition-all" style={{ background: isActive ? p.color : `${p.color}10` }}>
                    <p.icon className="h-5 w-5" accent={isActive ? "#fff" : p.color} style={{ color: isActive ? "#fff" : p.color }} />
                  </div>
                  <h3 className="mt-4 text-xl font-bold" style={{ fontFamily: FONT_DISPLAY, color: CHARCOAL }}>{p.name}</h3>
                  <p className="mt-1 text-sm leading-relaxed" style={{ color: `${CHARCOAL}70`, fontFamily: FONT_SANS }}>{p.desc}</p>
                </div>
                <div className="absolute bottom-0 left-0 h-[2px] transition-all duration-300" style={{ width: isActive ? "100%" : "0%", background: p.color }} />
              </motion.button>
            );
          })}
        </div>

        {/* Detail panel */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.35 }}
            className="mt-6 overflow-hidden rounded-3xl border bg-white"
            style={{ borderColor: `${active.color}15`, boxShadow: `0 8px 40px ${active.color}08` }}
          >
            <div className="h-[3px]" style={{ background: `linear-gradient(90deg, ${active.color}, ${active.color}66)` }} />
            <div className="p-8 sm:p-10">
              <div className="flex items-center gap-5">
                <div className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl" style={{ background: active.color }}>
                  <ActiveIcon className="h-8 w-8" accent="#fff" style={{ color: "#fff" }} />
                </div>
                <div>
                  <h3 className="text-3xl font-bold" style={{ fontFamily: FONT_DISPLAY, color: CHARCOAL }}>TriSphere {active.name}</h3>
                  <p className="mt-1 text-sm" style={{ color: `${CHARCOAL}70`, fontFamily: FONT_SANS }}>{active.desc}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}

function FlowSection() {
  return (
    <section className="relative py-24 sm:py-32" style={{ background: CREAM2 }}>
      <div className="pointer-events-none absolute inset-0 opacity-[0.03]" style={{ backgroundImage: GRAIN, backgroundSize: "200px 200px" }} />
      <div className="mx-auto max-w-5xl px-6 sm:px-8">
        <div className="mb-6 flex items-center gap-5">
          <div className="h-px w-10" style={{ background: WINE }} />
          <span className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: WINE, fontFamily: FONT_SANS }}>Data Flow</span>
        </div>
        <h2 style={{ fontFamily: FONT_DISPLAY, color: CHARCOAL, lineHeight: 0.95, letterSpacing: "-0.03em", fontSize: "clamp(2rem, 5vw, 4rem)", fontWeight: 700 }}>
          Everything flows through one ledger.
        </h2>
        <div className="mt-16 flex flex-wrap items-center justify-center gap-3">
          {flow.map((node, i) => (
            <motion.div key={node} initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.06, type: "spring", stiffness: 100 }} className="flex items-center gap-3">
              <div className="rounded-none px-5 py-2.5 text-sm font-medium" style={{ borderColor: i === flow.length - 1 ? `${SAND}40` : `${CHARCOAL}12`, background: i === 0 || i === flow.length - 1 ? `${SAND}15` : CREAM, color: i === 0 || i === flow.length - 1 ? WINE : CHARCOAL, fontFamily: FONT_SANS }}>
                {node}
              </div>
              {i < flow.length - 1 && <ArrowRightIcon className="h-4 w-4" accent={`${CHARCOAL}30`} style={{ color: `${CHARCOAL}30` }} />}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ArchitectureSection() {
  const layers = [
    { title: "Identity Layer", items: ["Digital student cards", "Biometric verification", "Role-based access", "National ID integration"], color: WINE, icon: ShieldIcon },
    { title: "Experience Layer", items: ["Parent portal", "Student app", "School admin console", "Merchant dashboard"], color: SAND, icon: DeviceIcon },
    { title: "Intelligence Layer", items: ["Real-time analytics", "AI predictions", "Custom reports", "Open APIs"], color: WINE, icon: ChartIcon },
    { title: "Payments Layer", items: ["MTN MoMo & Airtel", "Card processing", "Auto-reconciliation", "Sub-wallets"], color: SAND, icon: ReceiptIcon },
  ];
  return (
    <section className="py-24 sm:py-32">
      <div className="mx-auto max-w-5xl px-6 sm:px-8">
        <div className="mb-6 flex items-center gap-5">
          <div className="h-px w-10" style={{ background: WINE }} />
          <span className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: WINE, fontFamily: FONT_SANS }}>Architecture</span>
        </div>
        <h2 style={{ fontFamily: FONT_DISPLAY, color: CHARCOAL, lineHeight: 0.95, letterSpacing: "-0.03em", fontSize: "clamp(2rem, 5vw, 4rem)", fontWeight: 700 }}>
          Four layers, engineered for scale.
        </h2>
        <div className="mt-14 space-y-4">
          {layers.map((l, i) => (
            <motion.div key={l.title} initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ delay: i * 0.1, duration: 0.5, ease: EASE }} className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center" style={{ border: `1.5px solid ${CHARCOAL}12`, background: CREAM, boxShadow: "none" }}>
              <div className="flex shrink-0 items-center gap-4 sm:w-64">
                <div className="grid h-12 w-12 place-items-center rounded-xl" style={{ background: `${l.color}15` }}>
                  <l.icon className="h-6 w-6" accent={l.color} style={{ color: l.color }} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider" style={{ color: `${CHARCOAL}50`, fontFamily: FONT_SANS }}>Layer {i + 1}</div>
                  <h3 className="text-lg font-bold" style={{ fontFamily: FONT_DISPLAY, color: CHARCOAL }}>{l.title}</h3>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {l.items.map(item => <span key={item} className="rounded-none px-3 py-1.5 text-xs font-medium" style={{ border: `1px solid ${CHARCOAL}12`, background: CREAM2, fontFamily: FONT_SANS, color: `${CHARCOAL}80` }}>{item}</span>)}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ScaleSection() {
  const metrics = [
    { value: 126, suffix: "+", label: "Institutions" },
    { value: 50000, suffix: "+", label: "Students" },
    { value: 100000, suffix: "+", label: "Parents" },
    { value: 800, suffix: "+", label: "Merchants" },
    { value: 1000000, suffix: "+", label: "Transactions" },
    { value: 999, suffix: "%", label: "Uptime", decimals: 1 },
  ];
  return (
    <section className="relative overflow-hidden py-24 sm:py-32" style={{ background: CHARCOAL }}>
      <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{ backgroundImage: GRAIN, backgroundSize: "200px 200px" }} />
      <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(ellipse 60% 40% at 30% 20%, ${WINE}18 0%, transparent 50%), radial-gradient(ellipse 50% 40% at 70% 80%, ${SAND}10 0%, transparent 50%)` }} />
      <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full blur-3xl" style={{ background: `radial-gradient(circle, ${SAND}20 0%, transparent 60%)` }} />
      <div className="mx-auto max-w-5xl px-6 sm:px-8">
        <h2 className="text-center" style={{ fontFamily: FONT_DISPLAY, color: "white", lineHeight: 0.95, letterSpacing: "-0.03em", fontSize: "clamp(2rem, 5vw, 4rem)", fontWeight: 700 }}>An ecosystem at scale</h2>
        <div className="mt-14 grid grid-cols-2 gap-8 sm:grid-cols-3 lg:grid-cols-6">
          {metrics.map((m, i) => (
            <motion.div key={m.label} initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.08, ease: EASE }} className="text-center">
              <div style={{ fontFamily: FONT_DISPLAY, color: "white", fontSize: "clamp(1.5rem, 3vw, 2.5rem)", fontWeight: 700 }}>
                <Counter target={m.value} suffix={m.suffix} decimals={m.decimals ?? 0} />
              </div>
              <div className="mt-2 text-xs font-medium uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.4)", fontFamily: FONT_SANS }}>{m.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="relative overflow-hidden py-24 sm:py-32" style={{ background: WINE }}>
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: GRAIN, backgroundSize: "200px 200px" }} />
      <div className="absolute top-0 left-0 right-0 h-2" style={{ background: SAND }} />
      <div className="absolute bottom-0 left-0 right-0 h-2" style={{ background: SAND }} />
      <div className="mx-auto max-w-3xl px-6 text-center">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: EASE }}>
          <h2 style={{ fontFamily: FONT_DISPLAY, color: "white", lineHeight: 0.92, letterSpacing: "-0.03em", fontSize: "clamp(2.5rem, 7vw, 5rem)", fontWeight: 700 }}>Ready to join the ecosystem?</h2>
          <p className="mt-4 text-lg" style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", color: "rgba(255,255,255,0.65)" }}>Get started in minutes. No setup fees.</p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild size="lg" className="h-12 rounded-none px-8 text-[13px] font-bold uppercase tracking-[0.15em] hover:opacity-90" style={{ background: SAND, color: CHARCOAL, border: "none", fontFamily: FONT_SANS }}>
              <Link to="/auth">Get Started <ArrowRightIcon className="ml-2 h-4 w-4" accent={CHARCOAL} style={{ color: CHARCOAL }} /></Link>
            </Button>
            <Button asChild variant="ghost" size="lg" className="h-12 rounded-none px-7 text-[13px] font-bold uppercase tracking-[0.15em] text-white hover:bg-white/10" style={{ border: `1.5px solid ${SAND}50`, fontFamily: FONT_SANS }}>
              <Link to="/insights">View Insights <ChartIcon className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Counter({ target, suffix = "", decimals = 0 }: { target: number; suffix?: string; decimals?: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [display, setDisplay] = useState(0);
  const spring = useSpring(0, { stiffness: 60, damping: 20 });
  useEffect(() => { if (inView) spring.set(target); }, [inView, target, spring]);
  useEffect(() => { return spring.on("change", (v) => setDisplay(v)); }, [spring]);
  const formatted = decimals > 0 ? display.toFixed(decimals) : target >= 1_000_000 ? `${(display / 1_000_000).toFixed(1)}M` : target >= 1_000 ? `${Math.round(display / 100) / 10}K+` : Math.round(display).toString();
  return <span ref={ref}>{formatted}{suffix}</span>;
}

export default EcosystemPage;
